"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  archive,
  createGrant,
  getAttentionCountsQueryKey,
  getGetConversationQueryKey,
  getGetPropertiesQueryKey,
  getListGrantsQueryKey,
  patchConversation,
  patchScorecardType,
  putProperties,
  relink,
  revokeGrant,
  unarchive,
} from "@/lib/api/generated";
import type {
  ConversationPatch,
  GrantCreate,
  PropertiesPut,
  RelinkIn,
  ScorecardTypePatch,
} from "@/lib/api/model";

import { CONVERSATIONS_QUERY_PREFIX } from "../lib/query-keys";

export function useConversationMutations(id: string) {
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({
      queryKey: getGetConversationQueryKey(id),
      exact: true,
    });
    await qc.invalidateQueries({ queryKey: [...CONVERSATIONS_QUERY_PREFIX] });
    await qc.invalidateQueries({ queryKey: getAttentionCountsQueryKey() });
    await qc.invalidateQueries({ queryKey: getGetPropertiesQueryKey(id) });
    await qc.invalidateQueries({ queryKey: getListGrantsQueryKey(id) });
  };
  return {
    patch: useMutation({
      mutationFn: (b: ConversationPatch) => patchConversation(id, b),
      onSuccess: done,
    }),
    archive: useMutation({
      mutationFn: () => archive(id),
      onSuccess: done,
    }),
    unarchive: useMutation({
      mutationFn: () => unarchive(id),
      onSuccess: done,
    }),
    relink: useMutation({
      mutationFn: (b: RelinkIn) => relink(id, b),
      onSuccess: done,
    }),
    putProperties: useMutation({
      mutationFn: (b: PropertiesPut) => putProperties(id, b),
      onSuccess: done,
    }),
    setScorecardType: useMutation({
      mutationFn: (b: ScorecardTypePatch) => patchScorecardType(id, b),
      onSuccess: done,
    }),
    createGrant: useMutation({
      mutationFn: (b: GrantCreate) => createGrant(id, b),
      onSuccess: done,
    }),
    revokeGrant: useMutation({
      mutationFn: (grantId: string) => revokeGrant(id, grantId),
      onSuccess: done,
    }),
  };
}
